import { computed, watch } from 'vue';
import { useI18n, useUrl, useReportParams, useTableQuery } from '@/composables';
import { useStore } from '@/store';
import { DelimitersEnum } from '@/services/models/Enums';
import helpers from '@/services/helpers';
import { $emitter } from '@/services/emitter';
import type { LinkOptions, NextReportProps } from '@/components/query/url-types';

export const useMenuBase = () => {
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const url = useUrl();
  const { t } = useI18n();

  const maxSimultaneouslyDrilldownCount = 50;
  const maxTotalDrilldownCount = 100;

  const computedType = url.computedType;
  const drilldown = url.drilldown;
  const reportLink = url.reportLink;
  const reportDrillCampaign = url.reportDrillCampaign;

  const is = (what: string) => computedType.value === what;

  const isReport = () => url.isReport.value;

  const getLink = (group: string, options?: LinkOptions) => url.getLink(group, options);

  const drilldownForward = (previousFilter: unknown = null, nextReportProps: NextReportProps = {}) =>
    url.drilldownForward(previousFilter, nextReportProps);

  const drilldownForwardNewTab = (previousFilter: unknown = null, nextReportProps: NextReportProps = {}) =>
    url.drilldownForwardNewTab(previousFilter, nextReportProps);

  const getReportLink = (nextReportProps = {}) => url.getReportLink(nextReportProps);

  const getGroupByMetric = (metric: string) => url.getGroupByMetric(metric);

  const drlldownMaxSimultaneouslyCondintion = computed(
    () => tableQueryApi.tableSelectedItems.value.length > maxSimultaneouslyDrilldownCount,
  );

  const drlldownMaxTotalCondintion = computed(() => {
    const drilldownTotal = drilldown.value
      .map((item) => {
        if (item.filterValues.includes(DelimitersEnum.drillFilterOptions)) {
          const filterValues = item.filterValues.split(DelimitersEnum.drillFilterOptions);
          return filterValues.length;
        }
        return 1;
      })
      .reduce((accumulator, item) => accumulator + item, 0);

    return tableQueryApi.tableSelectedItems.value.length + drilldownTotal > maxTotalDrilldownCount;
  });

  const checkDrildown = () => {
    if (drlldownMaxSimultaneouslyCondintion.value) {
      $emitter.emit(
        'toast-error',
        t('The report can be created basing on {0} elements only', [
          maxSimultaneouslyDrilldownCount,
        ]),
      );
    } else if (drlldownMaxTotalCondintion.value) {
      $emitter.emit(
        'toast-error',
        t('The report can be created basing on {0} elements total', [
          maxTotalDrilldownCount,
        ]),
      );
    }

    return !(drlldownMaxSimultaneouslyCondintion.value || drlldownMaxTotalCondintion.value);
  };

  const report = (item?: unknown, grouping?: string) => {
    if (!checkDrildown()) return;

    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      const row = item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
      const link =
        row && (grouping || row.DT_RowId || row.id)
          ? getReportLink({
              tableNextReport: row,
              tableNextReportGroup: getGroupByMetric(grouping),
            })
          : reportLink.value;

      $emitter.emit('tab-add', link);
    });
  };

  const reportExclude = (item?: unknown, grouping?: string) => {
    if (!checkDrildown()) return;

    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      const link = getReportLink({
        tableNextReportGroup: getGroupByMetric(grouping),
        tableNextReportExclude: true,
      });

      $emitter.emit('tab-add', link);
    });
  };

  const isNotesVisible = computed(() => is('campaigns') || reportDrillCampaign.value);

  watch(() => tableQueryApi.tableSelectedItems.value, (items) => {
    if (tableQueryApi.tableNotesOpened.value && items.length !== 1) {
      store.setTableNotesOpened(false);
    }
  });

  const editDrilldownActionText = (type: string) => {
    switch (type) {
      case 'campaign':
      case 'campaigns':
        return t('Edit Campaign');
      case 'offer':
      case 'offers':
        return t('Edit Offer');
      case 'flow':
      case 'flows':
        return t('Edit Flow');
      case 'landing':
      case 'landings':
        return t('Edit Landing');
      case 'trafficSource':
      case 'trafficSources':
        return t('Edit Traffic Sources');
      case 'affiliateNetwork':
      case 'affiliateNetworks':
        return t('Edit Affiliate Network');
      case 'trackingDomain':
        return t('Edit Tracking Domain');
      default:
        return '';
    }
  };

  return {
    store,
    url,
    computedType,
    drilldown,
    reportLink,
    reportDrillCampaign,
    is,
    isReport,
    getLink,
    drilldownForward,
    drilldownForwardNewTab,
    getReportLink,
    getGroupByMetric,
    checkDrildown,
    report,
    reportExclude,
    isNotesVisible,
    editDrilldownActionText,
  };
};
